import { CircularProgress, Drawer } from '@mui/material';
import classnames from 'classnames';
import { compact, filter, includes, isEmpty, map, min, range } from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { formatNumber } from 'js/lib/FormatUtils';
import { DATA_CHARS_ENABLED } from 'js/constants/config';
import DateUtils from 'js/lib/DateUtils';
import { ReactComponent as InfoIcon } from 'new/components/icons/fi-rr-info.svg';
import { ReactComponent as RefreshIcon } from 'new/components/icons/refresh.svg';
import { IconButton } from 'new/elements/IconButton/IconButton';
import {
  RECIPE_GROUP_ANNOTATIONS,
  RECIPE_GROUP_DEFAULTS,
  RECIPE_GROUP_FILTERS,
  RECIPE_GROUP_TRANSFORMS,
  RECIPE_GROUP_VALIDATIONS,
  SchemaBrowserType,
  SchemaEditorSampleError,
} from 'js/constants/schemaEditor';
import { RecipeGroup, SchemaAttr, getSampleValue } from 'js/lib/RecipeBuilderUtils';
import Tooltip from '../elements/mui/Tooltip';
import SchemaSpecIcon from '../elements/schema-editor/icons/SchemaSpecIcon';
import AttributeDescriptionEditor from './AttributeDescriptionEditor';
import RecipeBuilderAttributeTypes from './RecipeBuilderAttributeTypes';
import RecipeGroupsList from './RecipeGroupsList';
import InputSamplesIcon from './icons/InputSamplesIcon';
import OutputSamplesIcon from './icons/OutputSamplesIcon';

import { fetchCharacteristics } from 'js/actions';
import { GET_DATA_CHARACTERISTICS_REQUEST } from 'js/constants/actionTypes';
import { setExpandedRecipe, setExpandedRecipeGroup } from 'js/actions';

interface Props {
  dataSet?: any;
  gridId: string;
  attr: SchemaAttr;
  samples: any;
  showCharacteristics?: boolean;
  isOpen: boolean;
  onClose: any;

  // from redux
  recipeGroups: RecipeGroup[];
  characteristics: any;
  isLoadingCharacteristics: boolean;
  setExpandedRecipeGroup: any;
  setExpandedRecipe: any;
  fetchCharacteristics: any;
}

const CHARACTERISTIC_INFO_BY_NAME = {
  topValues: {
    label: 'Top 10 Values',
    description: '10 most common values of this attribute',
  },
  nulls: {
    label: 'Nulls',
    description: 'Null values of this attribute',
  },
  sum: {
    label: 'Sum',
    description: 'Sum of all values of this attribute',
  },
  stddev: {
    label: 'Standard Deviation',
    description: 'Measure of data dispersion',
  },
  mean: {
    label: 'Mean',
    description: 'Mean of all values of this attribute',
  },
  max: {
    label: 'Max',
    description: 'Maximal value of this attribute',
  },
  min: {
    label: 'Min',
    description: 'Minimal value of this attribute',
  },
};

const AttributeInfoDrawer = (props: Props) => {
  const { dataSet, gridId, attr, isOpen, onClose } = props;
  let icon;

  if (isOpen) {
    switch (gridId) {
      case SchemaBrowserType.INPUT:
        icon = <InputSamplesIcon />;
        break;

      case SchemaBrowserType.OUTPUT:
        icon = <OutputSamplesIcon />;
        break;

      case SchemaBrowserType.SCHEMA_SPEC:
        icon = <SchemaSpecIcon />;
        break;

      default:
    }
  }

  return (
    <Drawer className="recipe-builder-info-drawer" open={isOpen} anchor="right" onClose={onClose}>
      <div className="recipe-builder-info-content">
        <header>
          <div className="top-row">
            {icon}
            <span className="attr-name">{attr?.__displayName}</span>
          </div>
          <RecipeBuilderAttributeTypes attr={attr} />
          {!attr?.__isMetaData && (
            <AttributeDescriptionEditor gridId={gridId} dataSet={dataSet} attr={attr} />
          )}
        </header>
        <SamplesSection {...props} />
        {DATA_CHARS_ENABLED && <CharacteristicsSection {...props} />}
        <RulesSection {...props} />
      </div>
    </Drawer>
  );
};

const SamplesSection = ({ attr, samples, isOpen }: Props) => (
  <div className="info-section">
    <h3>Sample Values From Nexset</h3>
    <hr />
    <ul className="samples-list">
      {isOpen &&
        map(range(min([samples?.length || 0, 5])), (sampleIndex) => {
          const { value, error } = getSampleValue(samples, attr, sampleIndex);
          let valueDisplay;

          switch (error) {
            case SchemaEditorSampleError.NULL_VALUE:
              valueDisplay = 'null';
              break;

            case SchemaEditorSampleError.MISSING_VALUE:
              valueDisplay = 'Missing value';
              break;

            case SchemaEditorSampleError.UNDEFINED_VALUE:
              valueDisplay = 'undefined';
              break;

            case SchemaEditorSampleError.FILTERED_OUT:
              valueDisplay = 'Record filtered out';
              break;

            default:
              valueDisplay = value;
          }

          return (
            <li key={sampleIndex} className={classnames({ 'sample-error': error })}>
              {valueDisplay}
            </li>
          );
        })}
    </ul>
  </div>
);

const CharacteristicsSection = ({
  dataSet,
  attr,
  characteristics,
  isOpen,
  isLoadingCharacteristics,
  showCharacteristics,
  fetchCharacteristics,
}: Props) => {
  if (!showCharacteristics) {
    return null;
  }

  const { asOf, count, byAttr } = characteristics || {};
  const attrCharacteristics = byAttr?.[attr?.__name];
  const {
    topValues,
    nulls,
    count: characteristicCount,
    ...otherCharacteristics
  } = attrCharacteristics || {};

  const onRefresh = () => {
    if (dataSet) {
      fetchCharacteristics(dataSet.id);
    }
  };

  return (
    <div className="info-section">
      <div className="heading-container">
        <h3>Nexset Data Profile</h3>
        <div className="characteristics-header-subtext">
          {asOf &&
            `${formatNumber(
              count || 0,
            )} records as of ${DateUtils.convertDateStringToFormat(asOf)}`}
          {isLoadingCharacteristics ? (
            <div className="refresh-characteristics-loader">
              <CircularProgress size={16} />
            </div>
          ) : (
            <IconButton className="refresh-characteristics-button" onClick={onRefresh}>
              <RefreshIcon />
            </IconButton>
          )}
        </div>
      </div>
      <hr />
      {isEmpty(attrCharacteristics) && (
        <div className="empty-characteristics">
          {isLoadingCharacteristics
            ? 'Loading...'
            : 'Profiling is not available for this attribute.'}
        </div>
      )}
      {isOpen && (
        <ul className="samples-list characteristics-list">
          {topValues && (
            <li>
              <CharacteristicLabel name="topValues" />
              <BarChart
                data={map(topValues, ({ value, percentage }) => ({
                  width: `${percentage}%`,
                  label: value,
                  value: `${percentage}%`,
                }))}
                fillColor="primary"
              />
            </li>
          )}
          {nulls && nulls !== '0%' && (
            <li>
              <CharacteristicLabel name="nulls" />
              <BarChart data={[{ width: nulls }]} />
              <span className="null-percentage-display">{nulls}</span>
            </li>
          )}
          {!isEmpty(otherCharacteristics) && (
            <li>
              <table className="other-characteristics">
                <tbody>
                  {map(otherCharacteristics, (value: any, key: string) => (
                    <tr key={key}>
                      <td className="characteristic-label-cell">
                        <CharacteristicLabel name={key} />
                      </td>
                      <td>{typeof value === 'object' ? JSON.stringify(value) : value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

const CharacteristicLabel = ({ name }: { name: string }) => {
  const { label, description } = CHARACTERISTIC_INFO_BY_NAME[name] || {
    label: name,
  };

  return (
    <div className="characteristic-label">
      {label}
      {description && (
        // @ts-ignore
        <Tooltip title={description} placement="right" arrow>
          <InfoIcon className="info-icon" />
        </Tooltip>
      )}
    </div>
  );
};

const BarChart = ({
  data,
  fillColor = 'grayscale',
}: {
  data: {
    width: string | number;
    label?: string;
    value?: string;
  }[];
  fillColor?: 'grayscale' | 'primary';
}) => (
  <table className="bar-chart">
    <tbody>
      {map(data, ({ width, label, value }, index) => (
        <tr key={index}>
          <td className="bar-container">
            <div className="bar-label">{label}</div>
            <div className={`bar-fill bar-fill-${fillColor}`} style={{ width }} />
          </td>
          {value && <td className="bar-value">{value}</td>}
        </tr>
      ))}
    </tbody>
  </table>
);

const RulesSection = ({
  dataSet,
  gridId,
  attr,
  isOpen,
  onClose,

  // from redux
  recipeGroups,
  setExpandedRecipeGroup,
  setExpandedRecipe,
}: Props) => {
  let attrRecipeGroups;

  if (isOpen && gridId === SchemaBrowserType.OUTPUT) {
    function addIndexToRecipe(recipe, index) {
      return { ...recipe, index };
    }

    attrRecipeGroups = compact(
      map(
        filter(
          map(recipeGroups, (recipeGroup, index) => ({
            ...recipeGroup,
            index,
          })),
          ({ type }) =>
            [
              RECIPE_GROUP_TRANSFORMS,
              RECIPE_GROUP_DEFAULTS,
              RECIPE_GROUP_FILTERS,
              RECIPE_GROUP_VALIDATIONS,
              RECIPE_GROUP_ANNOTATIONS,
            ].includes(type),
        ),
        (recipeGroup) => {
          const { type, data } = recipeGroup;
          let filteredData;

          switch (type) {
            case RECIPE_GROUP_TRANSFORMS:
            case RECIPE_GROUP_DEFAULTS:
              filteredData = filter(map(data as any[], addIndexToRecipe), {
                __destAttr: attr.__name,
              });
              break;

            case RECIPE_GROUP_FILTERS:
              filteredData = [
                {
                  ...data[0],
                  parameters: filter(map(data[0].parameters, addIndexToRecipe), {
                    ruleAttribute: attr.__name,
                  }),
                },
              ];
              break;

            case RECIPE_GROUP_VALIDATIONS:
              filteredData = filter(map(data as any[], addIndexToRecipe), ({ attrs }) =>
                includes(attrs, attr.__name),
              );
              break;

            case RECIPE_GROUP_ANNOTATIONS:
              filteredData = filter(map(data as any[], addIndexToRecipe), {
                attr: attr.__name,
              });
              break;

            default:
          }

          if (
            (type === RECIPE_GROUP_FILTERS && isEmpty(filteredData[0].parameters)) ||
            isEmpty(filteredData)
          ) {
            return null;
          }

          return {
            ...recipeGroup,
            data: filteredData,
          };
        },
      ),
    );
  }

  const _handleRecipeClicked = (recipeGroupIndex, recipeIndex) => {
    onClose();
    setExpandedRecipeGroup(recipeGroupIndex);
    setExpandedRecipe(recipeIndex);
  };

  if (isEmpty(attrRecipeGroups)) {
    return null;
  }

  return (
    <div className="info-section">
      <h3>Relevant Rules For This Attribute</h3>
      <hr />
      <div className="configure-recipe">
        <div className="configure-recipe-body">
          <RecipeGroupsList
            dataSet={dataSet}
            recipeGroups={attrRecipeGroups}
            isReadOnly
            onRecipeClicked={_handleRecipeClicked}
          />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const {
    recipeBuilder: { recipeGroups },
    samples,
    request,
  } = state;
  const { dataSet } = ownProps;
  const characteristics = samples[dataSet?.id]?.characteristics;
  const isLoadingCharacteristics = !!request[GET_DATA_CHARACTERISTICS_REQUEST];

  return {
    recipeGroups,
    characteristics,
    isLoadingCharacteristics,
  };
};

export default connect(mapStateToProps, {
  fetchCharacteristics,
  setExpandedRecipeGroup,
  setExpandedRecipe,
})(AttributeInfoDrawer);
